/**
 * Utilities for converting between DOM and SVG coordinate systems
 */

import type { ItemBounds, Point } from '../types';

/**
 * Get item bounds in timeline container coordinates
 * Accounts for scroll offsets and positioning
 */
export function getItemBoundsInTimeline(
  itemElement: HTMLElement,
  timelineContainerElement: HTMLElement,
): ItemBounds {
  const itemRect = itemElement.getBoundingClientRect();
  const containerRect = timelineContainerElement.getBoundingClientRect();

  // Get item position relative to timeline container viewport
  const relativeX = itemRect.left - containerRect.left;
  const relativeY = itemRect.top - containerRect.top;

  // Add scroll offsets to get position in the full scrollable content coordinate system
  const scrollX = timelineContainerElement.scrollLeft;
  const scrollY = timelineContainerElement.scrollTop;

  const itemId = Number.parseInt(itemElement.dataset.itemId || '0', 10);
  const rowId = itemElement.dataset.rowId || '';
  const columnId = itemElement.dataset.columnId || '';

  return {
    itemId,
    x: relativeX + scrollX,
    y: relativeY + scrollY,
    width: itemRect.width,
    height: itemRect.height,
    rowId,
    columnId,
  };
}

/**
 * Convert mouse event coordinates to SVG coordinates
 * Handles SVG viewBox transformations and scroll offsets
 */
export function mouseEventToSVGCoordinates(
  event: MouseEvent | React.MouseEvent,
  svgElement: SVGSVGElement,
): Point {
  // Get the SVG's position on screen
  const svgRect = svgElement.getBoundingClientRect();

  // Calculate position relative to SVG
  // This automatically handles scroll offsets because getBoundingClientRect
  // returns viewport-relative coordinates
  const x = event.clientX - svgRect.left;
  const y = event.clientY - svgRect.top;

  return { x, y };
}

/**
 * Get DOM coordinates from SVG coordinates
 * Useful for positioning DOM elements based on SVG calculations
 */
export function svgToDOMCoordinates(point: Point, svgElement: SVGSVGElement): Point {
  const svgPoint = svgElement.createSVGPoint();
  svgPoint.x = point.x;
  svgPoint.y = point.y;

  const ctm = svgElement.getScreenCTM();
  if (!ctm) {
    return point;
  }

  const transformed = svgPoint.matrixTransform(ctm);

  return {
    x: transformed.x,
    y: transformed.y,
  };
}

/**
 * Calculate bubble positions for an item
 * Returns left and right connection points
 */
export function getBubblePositions(
  itemBounds: ItemBounds,
  laneHeight: number,
  itemHeight: number,
): { left: Point; right: Point } {
  // Bubbles are positioned at vertical center of the color bar
  const colorBarTop = itemBounds.y + laneHeight / 2 - itemHeight / 2;
  const verticalOffset = 2; // slight offset to center within the bubble
  const verticalCenter = colorBarTop + itemHeight / 2 + verticalOffset;
  const leftMarginOffset = 8;
  const rightMarginOffset = 8;

  return {
    left: {
      x: itemBounds.x - leftMarginOffset,
      y: verticalCenter,
    },
    right: {
      x: itemBounds.x + itemBounds.width - rightMarginOffset,
      y: verticalCenter,
    },
  };
}

export function distance(p1: Point, p2: Point): number {
  return Math.hypot(p2.x - p1.x, p2.y - p1.y);
}
